import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { DropdownCardSelector } from 'components-ref/molecules/wallet/SelectCard/DropdownCardSelector'
import { getListCards } from 'services/endpoints/payment'
import { useUserProfile } from 'store/auth'
import colors from 'styles/colors'
import { type IProfileCard } from 'types/interfaces/IProfile'

interface PaymentModalProps {
  purchaseCb: () => void
  paymentMethod: {
    method: 'card' | 'deposit'
    cardId: string | undefined
  }
  setPaymentMethod: (method: {
    method: 'card' | 'deposit'
    cardId: string | undefined
  }) => void
  price: number
}

export function PaymentModal({
  purchaseCb,
  paymentMethod,
  setPaymentMethod: selectCard,
  price
}: PaymentModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const userData = useUserProfile()!

  const [taxAmount, setTaxAmount] = useState(0)
  const [taxRate, setTaxRate] = useState<number | null>(null)

  const profileCards = Array.isArray(userData?.cards) ? userData.cards : []
  const hasProfileCards = profileCards.length > 0

  const { data: cardList } = useQuery(['allCards'], getListCards, {
    enabled: !hasProfileCards,
    refetchOnWindowFocus: false
  })

  const cards = useMemo<IProfileCard[]>(
    () =>
      hasProfileCards
        ? profileCards
        : Array.isArray(cardList?.cards)
          ? cardList.cards
          : [],
    [hasProfileCards, profileCards, cardList?.cards]
  )

  const walletBalance = Number(userData?.wallet_deposit ?? 0)
  const walletCanCover = walletBalance >= price
  const hasCards = cards.length > 0
  const needsCard = !walletCanCover && !hasCards

  useEffect(() => {
    if (walletCanCover) {
      if (paymentMethod.method !== 'deposit' || paymentMethod.cardId !== undefined) {
        selectCard({ method: 'deposit', cardId: undefined })
      }

      return
    }

    if (hasCards) {
      const selectedCardStillExists =
        paymentMethod.method === 'card' &&
        !!paymentMethod.cardId &&
        cards.some(card => String(card.id) === String(paymentMethod.cardId))

      if (selectedCardStillExists) return

      const defaultCard =
        cards.find(card => card.is_default && !card.is_expired) ??
        cards.find(card => !card.is_expired) ??
        cards[0]

      if (defaultCard?.id != null) {
        selectCard({
          method: 'card',
          cardId: String(defaultCard.id)
        })
      }

      return
    }

    if (paymentMethod.method !== 'deposit' || paymentMethod.cardId !== undefined) {
      selectCard({ method: 'deposit', cardId: undefined })
    }
  }, [
    walletCanCover,
    hasCards,
    cards,
    paymentMethod.method,
    paymentMethod.cardId,
    selectCard
  ])

  useEffect(() => {
    if (paymentMethod.method !== 'card' || !paymentMethod.cardId) {
      setTaxRate(null)
      return
    }

    const selectedCard = cards.find(
      card => String(card.id) === String(paymentMethod.cardId)
    )

    setTaxRate(
      typeof selectedCard?.tax_rate === 'number'
        ? selectedCard.tax_rate
        : null
    )
  }, [cards, paymentMethod.method, paymentMethod.cardId])

  useEffect(() => {
    if (paymentMethod.method === 'card' && taxRate !== null) {
      setTaxAmount(Number(((price * taxRate) / 100).toFixed(2)))
      return
    }

    setTaxAmount(0)
  }, [price, taxRate, paymentMethod.method])

  const displayTotal =
    paymentMethod.method === 'card'
      ? price + taxAmount
      : price

  return (
    <Container>
      <DropdownCardSelector
        paymentMethod={paymentMethod}
        setPaymentMethod={selectCard}
        cardList={{ cards }}
        walletCanCover={walletCanCover}
        walletRequired={price}
      />

      <PriceWrapper>
        <TotalPrice>
          {t('total')}: ${displayTotal.toFixed(2)}
        </TotalPrice>

        {taxRate !== null && paymentMethod.method === 'card' && (
          <TaxRate className='subscriptionPriceTaxRate'>
            <TaxRateText className='text-gray-500'>Tax</TaxRateText>
            <TaxRateData>+${taxAmount.toFixed(2)}</TaxRateData>
          </TaxRate>
        )}
      </PriceWrapper>

      {paymentMethod.method === 'card' && (
        <StatementText>
          The charge on your credit card bill will appear as Innodev Solutions Inc
          on your credit card statement
        </StatementText>
      )}

      <Button
        onClick={() => {
          if (needsCard) {
            navigate('/settings/general/wallet')
            return
          }

          purchaseCb()
        }}
      >
        {needsCard ? t('add_card') : t('buy')}
      </Button>
    </Container>
  )
}

const Container = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: end;
  align-items: end;
  margin-bottom: 1rem;
  position: relative;
`

const TotalPrice = styled.p`
  color: ${colors.darkGray};
  font-size: 1.6rem;
  font-weight: 600;
  padding-right: 1rem;
`

const TaxRate = styled.div`
  position: absolute;
  transform: translate(0, -50%);
  right: 0;
  bottom: -25px;
  font-size: 12px;
  color: #5d5d5e;
  line-height: 1.2;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
`

const TaxRateText = styled.div`
  padding-left: 14px;
`

const TaxRateData = styled.div`
  margin-left: 0.5rem;
  font-weight: 500;
  font-size: 12px;
  color: #000;
  padding-right: 1rem;
`

const StatementText = styled.p`
  font-size: 11px;
  padding-bottom: 12px;
  padding-top: 16px;
`

const Button = styled.button`
  height: 4rem;
  width: 13.5rem;
  transition: opacity ease-in-out 0.3s;
  font-size: 1.4rem;
  background: ${colors.blue92deg};
  color: ${colors.white};
  border: 0;
  border-radius: 2rem;
`